import {Component, Input, OnInit, SimpleChanges} from "@angular/core";
import {CommonModule} from "@angular/common";
import {FlexModule} from "@angular/flex-layout/flex";
import {
  booleanToYesNo,
  ConsumerFinanceApplication,
  getApr,
  isInternalUser,
} from '@portal-workspace/grow-shared-library';
import {
  applicationToAdverseOnFile,
  applicationToAsset,
  applicationToAssetCondition,
  applicationToBalloonPayment,
  applicationToBalloonPaymentPrecentageNumber,
  applicationToBrokerOriginationFee,
  applicationToCrediRateAdjustment,
  applicationToDepositAmount,
  applicationToDocFee,
  applicationToDocFeeFinanced,
  applicationToEquifaxDirectorScoreMeetsThreshold,
  applicationToInvoiceAmount,
  applicationToLastUpdatedByUser,
  applicationToLoanAmount,
  applicationToLoanTerms,
  applicationToPaymentFrequency,
  applicationToPropertyOwner,
  applicationToRepaymentPlusMonthlyAccountKeepingFee,
  getUser,
} from "@portal-workspace/grow-ui-library";
import {UntilDestroy} from "@ngneat/until-destroy";
import {MatTableModule} from "@angular/material/table";
import {MatCardModule} from '@angular/material/card';
import {MessageBoxComponent} from '../../message-box/message-box.component';
import {LooseCurrencyPipe} from '../../../pipes/loose-currency.pipe';
import {PercentagePipe} from '../../../pipes/percentage.pipe';

// Read-only viewer for legacy Consumer Asset Finance applications. The product is decommissioned;
// no recalculation or editing is supported. Values are sourced from the saved application/PricingDetails.
@UntilDestroy()
@Component({
  selector: 'consumer-pricing',
  templateUrl: './consumer-pricing.component.html',
  styleUrls: ['./consumer-pricing.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FlexModule,
    MatTableModule,
    MatCardModule,
    MessageBoxComponent,
    LooseCurrencyPipe,
    PercentagePipe,
  ]
})
export class ConsumerPricingComponent implements OnInit {

  @Input({required: true}) application!: ConsumerFinanceApplication;

  user = getUser();
  isInternalUser = isInternalUser(this.user);

  lastEditedByUser?: string | null;

  dataSource: (
    {name: string, highlight?: (value: string | null | undefined) => boolean, type: 'text', value: string | null | undefined} |
    {name: string, highlight?: (value: number | string | null | undefined) => boolean, type: 'currency', value: number | string | null | undefined} |
    {name: string, highlight?: (value: number | string | null | undefined)=>boolean, type: 'percentage', value: number | string | null | undefined}
  )[] = [];

  ngOnInit() {
    this.createReadonlyTableDataSource();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['application']) {
      this.createReadonlyTableDataSource();
    }
  }

  private createReadonlyTableDataSource() {
    this.lastEditedByUser = applicationToLastUpdatedByUser(this.application)?.name;
    const asset = applicationToAsset(this.application);
    this.dataSource = [
      { name: 'Invoice Amount ($)', type: 'currency', value: applicationToInvoiceAmount(this.application)},
      { name: 'Asset Type', type: 'text', value: asset?.type?.value},
      { name: 'Asset Make', type: 'text', value: asset?.make ?? 'N/A' },
      { name: 'Asset Year', type: 'text', value: asset?.year },
      { name: 'Asset Description', type: 'text',  value: asset?.description},
      { name: 'Asset Condition', type: 'text', value: applicationToAssetCondition(this.application)?.type},
      { name: 'Loan Terms', type: 'text', value: `${applicationToLoanTerms(this.application)?.type ?? ''} months`},
      { name: 'Payment Frequency', type: 'text', value: applicationToPaymentFrequency(this.application)?.type },
      { name: 'Repayment Amount ($)', type: 'currency', value: applicationToRepaymentPlusMonthlyAccountKeepingFee(this.application)},
      { name: 'Property Owner', type: 'text', value: booleanToYesNo(applicationToPropertyOwner(this.application))},
      { name: 'Adverse on file', type: 'text', value: booleanToYesNo(applicationToAdverseOnFile(this.application))},
      { name: 'Director Equifax Score > 600', type: 'text', value: booleanToYesNo(applicationToEquifaxDirectorScoreMeetsThreshold(this.application, '>=600'))},
      { name: 'Deposit Amount ($)', type: 'currency', value: applicationToDepositAmount(this.application)},
      { name: 'Loan Amount $)', type: 'currency', value: applicationToLoanAmount(this.application)},
      { name: 'Interest Rate (%)', type: 'percentage', value: getApr(this.application)},
      { name: 'Doc Fee Financed', type: 'text', value: booleanToYesNo(applicationToDocFeeFinanced(this.application))},
      { name: 'Doc Fee ($)', type: 'currency', value: applicationToDocFee(this.application)},
      { name: 'Balloon Payment Due at maturity (%)', type: 'percentage', value: applicationToBalloonPaymentPrecentageNumber(this.application)},
      { name: 'Balloon Payment(Residual) ($)', type: 'currency', value: applicationToBalloonPayment(this.application)},
      { name: 'Credit Assistance Fee ($)', type: 'currency', value: applicationToBrokerOriginationFee(this.application)},
      { name: 'Credit Rate Adjustment', highlight: (v) => Number(v) != 0, type: 'percentage', value: applicationToCrediRateAdjustment(this.application) },
    ];
  }
}
